import { IconCheck, IconInfo } from '@developerz/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { createDonation } from '../lib/donations';
import { addKey } from '../lib/keys';
import { createMutation } from '../lib/mutation';
import { type DonateKeyDraft, DonateKeyForm } from './DonateKeyForm';

// Step 4 of the donate wizard (arc 101 slice 11, plan doc 05): pin an inference
// key. Container — it owns the TWO round-trips the gift takes, and the one rule
// that makes a retry safe.
//
// Capturing the key (`POST /v1/billing/keys`) and donating it
// (`POST /v1/donations`) are separate writes, and only the second can be refused
// by the paywall. If the first succeeded and the second failed, the key EXISTS —
// resubmitting the form must not mint a second copy of the same credential, so
// the captured id is held and reused. That is the local face of "a step failing
// does not roll back the earlier ones".

export type DonateKeyStepProps = {
  readonly project: string;
  readonly token: string | null;
  /** A live `llm` gift is already pinned to this project. */
  readonly done: boolean;
  readonly onDonated: () => void;
};

export function DonateKeyStep(props: DonateKeyStepProps): JSX.Element {
  // Survives a failed donate so a retry donates the key already captured rather
  // than sealing a second one under the same secret.
  const [keyId, setKeyId] = createSignal<string | null>(null);

  // The fallback covers only a failure with no sentence of its own: a 402
  // paywall and a 422 ceiling/target refusal already say what to fix, and
  // `errorReason` renders those rather than re-deriving them.
  const donate = createMutation({
    call: async (draft: DonateKeyDraft) => {
      const existing = keyId();
      const id = existing ?? (await addKey(draft.capture, props.token)).key.id;
      setKeyId(id);
      await createDonation(
        {
          kind: 'llm',
          target: 'project',
          repo_full_name: props.project,
          byok_key_id: id,
          models: draft.models,
        },
        props.token,
      );
    },
    fallback: t('donations.key_error'),
    onSuccess: () => {
      setKeyId(null);
      props.onDonated();
    },
  });

  return (
    <div class="field-group">
      <Show when={props.done}>
        <div class="alert alert--icon" role="status">
          <IconCheck />
          <p class="alert__title">{t('donations.key_done')}</p>
        </div>
      </Show>
      <p class="field__desc">{t('donations.key_intro')}</p>
      <div class="alert alert--icon" role="note">
        <IconInfo />
        <p class="alert__title">{t('donations.key_cap')}</p>
      </div>
      <DonateKeyForm
        busy={donate.pending()}
        error={donate.error()}
        onSubmit={(draft) => void donate.run(draft)}
      />
    </div>
  );
}
